import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const trustSignals = {
    averageRating: 4.8,
    totalProviders: 360,
    reviewCount: 1730
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2
      }
    }
  };

  // Animated background images representing sustainability
  const backgroundImages = [
    { icon: 'Sun', color: 'text-yellow-500', delay: 0, position: 'top-[10%] left-[5%]' },
    { icon: 'Zap', color: 'text-green-500', delay: 2, position: 'top-[20%] right-[8%]' },
    { icon: 'Home', color: 'text-blue-500', delay: 4, position: 'bottom-[25%] left-[10%]' },
    { icon: 'Leaf', color: 'text-emerald-500', delay: 1, position: 'top-[60%] right-[12%]' },
    { icon: 'Wind', color: 'text-cyan-500', delay: 3, position: 'bottom-[15%] right-[20%]' },
    { icon: 'Droplet', color: 'text-blue-400', delay: 5, position: 'top-[40%] left-[15%]' },
  ];

  return (
    <section className="relative bg-gradient-to-br from-green-50 via-blue-50 to-emerald-50 overflow-hidden min-h-[600px] sm:min-h-[700px]">
      {/* Animated Background with Sustainability Icons */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-emerald-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        
        {/* Floating Sustainability Icons */}
        {backgroundImages?.map((item, index) => (
          <motion.div
            key={index}
            className={`absolute ${item?.position} opacity-10`}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: [0.05, 0.15, 0.05],
              scale: [1, 1.2, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{
              duration: 8,
              delay: item?.delay,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          >
            <Icon name={item?.icon} size={80} className={item?.color} />
          </motion.div>
        ))}

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2316a34a%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
        <motion.div 
          className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Main Headline */}
          <div className="space-y-3 sm:space-y-4">
            <motion.h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight px-2"
              variants={itemVariants}
            >
              Onafhankelijk verduurzamingsadvies & betrouwbare aanbieders voor uw woning
            </motion.h1>
            
            <motion.p 
              className="text-lg sm:text-xl lg:text-2xl font-body text-muted-foreground max-w-3xl mx-auto px-2"
              variants={itemVariants}
            >
              Bespaar op energiekosten, verhoog uw wooncomfort en maak de juiste keuzes zonder verkooppraat
            </motion.p>
          </div>

          {/* Dual CTAs */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4"
            variants={itemVariants}
          >
            <Link to="/quote-request-form" className="w-full sm:w-auto">
              <motion.div variants={buttonVariants} whileHover="hover">
                <Button size="xl" iconName="FileText" iconPosition="left" fullWidth className="text-sm sm:text-base">
                  Offerte aanvragen
                </Button>
              </motion.div>
            </Link>
            <Link to="/leverancier-registration" className="w-full sm:w-auto">
              <motion.div variants={buttonVariants} whileHover="hover">
                <Button variant="outline" size="xl" iconName="Building" iconPosition="left" fullWidth className="text-sm sm:text-base">
                  <span className="hidden sm:inline">Aanbieder? Presenteer uw oplossing</span>
                  <span className="sm:hidden">Voor Aanbieders</span>
                </Button>
              </motion.div>
            </Link>
          </motion.div>

          {/* Trust Signals */}
          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 pt-4 px-4"
            variants={itemVariants}
          >
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                {[...Array(5)]?.map((_, i) => (
                  <Icon key={i} name="Star" size={14} className="text-yellow-500 fill-current sm:w-4 sm:h-4" />
                ))}
              </div>
              <span className="text-xs sm:text-sm font-body font-medium text-foreground">
                {trustSignals?.averageRating}/5
              </span>
              <span className="text-xs sm:text-sm font-caption text-muted-foreground">
                ({trustSignals?.reviewCount?.toLocaleString('nl-NL')} reviews)
              </span>
            </div>
            
            <div className="hidden sm:block w-px h-6 bg-border"></div>
            
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={14} className="text-primary sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-body text-muted-foreground">
                {trustSignals?.totalProviders?.toLocaleString('nl-NL')}+ beoordeelde aanbieders
              </span>
            </div>
            
            <div className="hidden sm:block w-px h-6 bg-border"></div>
            
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={14} className="text-success sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-body font-medium text-success">
                Onafhankelijk & transparant
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
      <style jsx>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;